// MA-1169 — the ONE curated table of what is tricky in each heart word, and the
// ONE function that turns an entry into the spoken callout.
//
// THE DEFECT this closes: buildDecodeUtterance used to build a heart word's
// callout from the graphemes NOT TAUGHT at the phase, each spoken by its
// REGULAR sound (GRAPHEME_TO_PHONEME_KEY). That is the sound the letters make
// in a regular word, not in this one: 'have' said "ay" (split-e a_e), 'one'
// said "oh", 'come'/'some' said "oh", and 'the' (every letter taught at R6, th
// untaught below it) named th but never its odd e. A heart word is a heart word
// BECAUSE some letters do not make their usual sound, so the callout has to say
// what those letters ACTUALLY say in this word. No rule can derive that; it is a
// per-word fact, so it lives in one hand-checked table keyed by word.
//
// SHAPE (operator decisions D-1101-5 and D-1101-7, one rule for every entry):
//   "<word>. The tricky part: <LETTERS> says <sound>."
//   several parts are joined by ", and "; a silent letter reads "<LETTERS> is quiet".
// LETTERS are spoken as letter NAMES, upper case. A SINGLE letter reads
// "the letter <X>" ("the letter A says uh", "the letter E is quiet"), so the
// narration voice never reads a lone capital A as the article "a" (D-1101-7,
// RED S-2). A multi-letter unit is hyphen-joined ("T-H says th", "A-I says eh").
// Every <sound> is a PHONEME_SAY key, so the narration voice says the same
// respelling ("uh", "ee", "oo") the sound-out lane already uses and the two can
// never drift apart. Every entry has at least one part (the type enforces it):
// there is no "special word" line and no whole-word line (D-1101-7 deleted the
// unreachable branch, RED N-1; 'one' is respelled letter by letter).
//
// COVERAGE: every word that gets the heart verdict (gradeWord) at any phase over
// the real served corpus has exactly one entry, and no entry is dead. Today that
// is all 38 heart words of HFW_LIST (every one is in the library corpus, and the
// library decode support enumerates R0-R5). src/engine/__tests__/
// ma1169HeartWordCallouts.test.mjs enforces both directions; a heart word with no
// entry is a test failure. At runtime a missing entry replays the plain word (no
// sound claim at all), never the old regular-sound guess.
//
// The callout no longer depends on the phase: what is irregular about 'have' is
// the same at R3 and at R5. Parts that are only TEMPORARILY tricky (a regular
// pattern the ladder has not taught when the word is scheduled: th, or, er, ow,
// ou, split-e in make/like) are named with their regular sound, which in those
// words IS the sound the letters make.
import { PHONEME_SAY } from "./webSpeechSource";

/** One tricky part of a heart word: the letters as written in the word, and
 *  either the PHONEME_SAY key of the sound they make there, or `quiet`. */
export type HeartWordPart =
  | { readonly letters: string; readonly say: string }
  | { readonly letters: string; readonly quiet: true };

export interface HeartWordEntry {
  /** The tricky parts, in word order (never empty). */
  readonly parts: readonly [HeartWordPart, ...HeartWordPart[]];
  /** Why this decomposition (the hand check, kept beside the data). */
  readonly why: string;
}

const say = (letters: string, key: string): HeartWordPart => ({ letters, say: key });
const quiet = (letters: string): HeartWordPart => ({ letters, quiet: true });

/** MA-1169 — keyed by the normalized (lower-case) word. */
export const HEART_WORD_IRREGULAR: Readonly<Record<string, HeartWordEntry>> = Object.freeze({
  a: { parts: [say("a", "/u/")], why: "the article a is said uh, not the short a of cat" },
  i: { parts: [say("i", "/I/")], why: "the word I says its name (long i), not the short i of sit" },
  is: { parts: [say("s", "/z/")], why: "s says z here" },
  the: { parts: [say("e", "/u/")], why: "the odd e: said uh, not the short e of bed (th is a regular digraph)" },
  to: { parts: [say("o", "/oo/")], why: "o says oo, not the short o of hot" },
  of: { parts: [say("o", "/u/"), say("f", "/v/")], why: "o says uh and f says v" },
  was: { parts: [say("a", "/u/"), say("s", "/z/")], why: "a says uh (not the short a of cat) and s says z" },
  we: { parts: [say("e", "/E/")], why: "e at the end of a short word says its name (ee), not the short e of bed" },
  he: { parts: [say("e", "/E/")], why: "e says ee, not the short e of bed" },
  me: { parts: [say("e", "/E/")], why: "e says ee, not the short e of bed" },
  be: { parts: [say("e", "/E/")], why: "e says ee, not the short e of bed" },
  she: { parts: [say("e", "/E/")], why: "e says ee (sh is a regular digraph)" },
  you: { parts: [say("ou", "/oo/")], why: "ou says oo here, not the ow of out (y is regular)" },
  have: { parts: [say("a", "/a/"), quiet("e")], why: "a keeps its short sound and the final e is quiet (it does not make a say ay, as in make)" },
  said: { parts: [say("ai", "/e/")], why: "ai says the short e of bed, not the ay of rain" },
  are: { parts: [say("ar", "/ar/"), quiet("e")], why: "ar says ar and the final e is quiet (it does not make a say ay)" },
  for: { parts: [say("or", "/or/")], why: "or says or (r-controlled vowel, not yet taught when the word is scheduled)" },
  with: { parts: [say("th", "/th/")], why: "th says th (digraph, not yet taught when the word is scheduled)" },
  what: { parts: [say("wh", "/w/"), say("a", "/u/")], why: "wh says w and a says uh, not the short a of cat" },
  here: { parts: [say("ere", "/ear/")], why: "ere says eer (the e is not short and the final e is not a split-e)" },
  they: { parts: [say("ey", "/A/")], why: "ey says ay here, not the ee of key (th is a regular digraph)" },
  their: { parts: [say("eir", "/air/")], why: "eir says air" },
  this: { parts: [say("th", "/th/")], why: "th says th (digraph, not yet taught when the word is scheduled)" },
  that: { parts: [say("th", "/th/")], why: "th says th (digraph, not yet taught when the word is scheduled)" },
  do: { parts: [say("o", "/oo/")], why: "o says oo, not the short o of hot" },
  my: { parts: [say("y", "/I/")], why: "y at the end says eye, not the y of yes" },
  by: { parts: [say("y", "/I/")], why: "y at the end says eye, not the y of yes" },
  his: { parts: [say("s", "/z/")], why: "s says z here" },
  her: { parts: [say("er", "/er/")], why: "er says er (r-controlled vowel, not yet taught when the word is scheduled)" },
  one: { parts: [say("o", "/w/"), quiet("e")], why: "the o makes the w sound at the start (wuh, then the n) and the final e is quiet (D-1101-7: respelled honestly, never 'sounds like won')" },
  two: { parts: [quiet("w"), say("o", "/oo/")], why: "w is quiet and o says oo" },
  out: { parts: [say("ou", "/ow/")], why: "ou says ow (vowel team, not yet taught when the word is scheduled)" },
  now: { parts: [say("ow", "/ow/")], why: "ow says ow (vowel team, not yet taught when the word is scheduled)" },
  down: { parts: [say("ow", "/ow/")], why: "ow says ow (vowel team, not yet taught when the word is scheduled)" },
  make: { parts: [say("a", "/A/"), quiet("e")], why: "split-e: a says its name (ay) and the final e is quiet" },
  like: { parts: [say("i", "/I/"), quiet("e")], why: "split-e: i says its name (eye) and the final e is quiet" },
  come: { parts: [say("o", "/u/"), quiet("e")], why: "o says uh (not the oh a split-e would give) and the final e is quiet" },
  some: { parts: [say("o", "/u/"), quiet("e")], why: "o says uh (not the oh a split-e would give) and the final e is quiet" },
});

/** Letters spoken as letter names: "ai" -> "A-I"; a single letter reads
 *  "the letter E" (D-1101-7), so a lone "A" is never voiced as the article. */
export function heartLettersSpoken(letters: string): string {
  return letters.length === 1 ? `the letter ${letters.toUpperCase()}` : letters.toUpperCase().split("").join("-");
}

/** The spoken callout for a heart word's table entry. */
export function heartWordCallout(word: string, entry: HeartWordEntry): string {
  const parts = entry.parts.map((p) =>
    "quiet" in p ? `${heartLettersSpoken(p.letters)} is quiet` : `${heartLettersSpoken(p.letters)} says ${PHONEME_SAY[p.say]}`,
  );
  return `${word}. The tricky part: ${parts.join(", and ")}.`;
}
